/**
 * ACTION BOARD — turns the live feeds into a short, ranked list of things that
 * actually need a decision right now, each with the evidence that triggered it
 * and a concrete next step.
 *
 * The dashboard already shows plenty of numbers. What it did not do was answer
 * the only question that matters at 3 AM: "what do I do?". This does.
 *
 * Alerting discipline is ported from FloodDash's `server/rainAlerts.js`, which
 * learned it the hard way running real LINE alert channels:
 *   · TOP BAND ONLY — a threshold that 55 of 78 provinces meet is not an alert.
 *     Every rule here fires on a genuinely actionable band, not on "elevated".
 *   · ABSOLUTE FLOORS — "×5 of nothing is nothing". A multiplier or a trend
 *     never fires on its own; there is always a real magnitude gate with it.
 *   · SEVERITY MUST RANK HONESTLY — a "look here now" signal must never outrank
 *     "the river is over the banks".
 * Plus one rule of our own: BLIND SPOTS ARE ALERTS. If a feed that drives these
 * decisions is down, say so — an empty board because nothing is reporting is a
 * dangerous thing to mistake for an empty board because all is well.
 *
 * Pure + dependency-free so every threshold is pinned by unit tests.
 */

import type {
  DamStatus,
  FloodGauge,
  PattaniRiverForecast,
  PrecipNowcast,
  YalaFloodOps,
  AirDangerProvince,
} from "@yala/shared";

export type ActionSeverity = "critical" | "warning" | "watch" | "blindspot";

/** Bilingual string — the engine stays pure, the component just picks a language. */
export interface Bilingual {
  en: string;
  th: string;
}

export interface ActionItem {
  id: string;
  severity: ActionSeverity;
  /** What is happening. */
  title: Bilingual;
  /** The measured numbers that tripped the rule — always concrete, never prose. */
  evidence: string;
  /** The next step a human should take. */
  action: Bilingual;
}

/** A feed the board depends on; `ok:false` means we are blind to that signal. */
export interface FeedHealth {
  id: string;
  label: Bilingual;
  ok: boolean;
}

export interface ActionBoardInput {
  dam: DamStatus | null;
  gauges: FloodGauge[];
  riverForecast: PattaniRiverForecast | null;
  precip: PrecipNowcast | null;
  floodOps: YalaFloodOps | null;
  airDanger: AirDangerProvince[];
  feeds: FeedHealth[];
}

// ── Thresholds ────────────────────────────────────────────────────────────
// Every number here is a decision boundary, named so it can be argued with.
export const THRESHOLDS = {
  /** Bang Lang Dam storage — spill risk becomes a live concern. */
  DAM_CRITICAL_PCT: 90,
  DAM_WARNING_PCT: 80,
  DAM_WATCH_PCT: 70,
  /** Rain in the next 2h. Heavy rain on saturated ground is what floods the city. */
  RAIN_CRITICAL_2H_MM: 30,
  RAIN_WARNING_2H_MM: 15,
  /** Forecast peak must BOTH multiply and clear an absolute floor (m³/s). */
  RIVER_SURGE_RATIO: 1.8,
  RIVER_SURGE_FLOOR_CMS: 50,
  /** Air-quality danger — only the top band pages. */
  AIR_PM25_WARNING: 75,
} as const;

function pct(n: number | null | undefined): string {
  return n == null || Number.isNaN(n) ? "—" : `${Math.round(n)}%`;
}
function num(n: number | null | undefined, digits = 0, unit = ""): string {
  return n == null || Number.isNaN(n) ? "—" : `${n.toFixed(digits)}${unit}`;
}

/** Rank order for sorting — lower sorts first. */
const SEVERITY_RANK: Record<ActionSeverity, number> = {
  critical: 0,
  warning: 1,
  watch: 2,
  blindspot: 3,
};

/**
 * Evaluate every rule. Returns a ranked list, most severe first; an empty list
 * genuinely means "nothing is over threshold" (blind spots surface as items, so
 * an empty board is never hiding a dead feed).
 */
export function evaluateActionBoard(input: ActionBoardInput): ActionItem[] {
  const items: ActionItem[] = [];
  const { dam, gauges, riverForecast, precip, floodOps, airDanger, feeds } = input;

  // ── People already displaced — the highest-order fact on the board ───────
  if (floodOps && floodOps.evacueesTotal > 0) {
    const top = floodOps.topShelters[0];
    items.push({
      id: "evacuees-active",
      severity: "critical",
      title: {
        en: `${floodOps.evacueesTotal.toLocaleString()} people are in shelters right now`,
        th: `มีผู้อพยพ ${floodOps.evacueesTotal.toLocaleString()} คนในศูนย์พักพิงขณะนี้`,
      },
      evidence: `${floodOps.evacueesTotal.toLocaleString()} evacuees · ${floodOps.shelterCount} shelters${top ? ` · largest: ${top.name} (${top.persons.toLocaleString()})` : ""}`,
      action: {
        en: "Confirm food, water and power at the largest shelters; check capacity headroom before the next rain band.",
        th: "ตรวจสอบอาหาร น้ำ และไฟฟ้าที่ศูนย์พักพิงขนาดใหญ่ และเช็คความจุที่เหลือก่อนฝนรอบถัดไป",
      },
    });
  }

  // ── Bang Lang Dam ───────────────────────────────────────────────────────
  if (dam?.storagePct != null) {
    const s = dam.storagePct;
    if (s >= THRESHOLDS.DAM_CRITICAL_PCT) {
      items.push({
        id: "dam-critical",
        severity: "critical",
        title: { en: "Bang Lang Dam near spill capacity", th: "เขื่อนบางลางใกล้ความจุสูงสุด" },
        evidence: `storage ${pct(s)} (≥${THRESHOLDS.DAM_CRITICAL_PCT}%) · outflow ${num(dam.outflowCms, 0, " m³/s")}`,
        action: {
          en: "Contact EGAT on release scheduling and pre-warn downstream Bannang Sata / Mueang Yala riverside communities.",
          th: "ประสาน กฟผ. เรื่องแผนระบายน้ำ และแจ้งเตือนชุมชนริมน้ำบันนังสตา/เมืองยะลาล่วงหน้า",
        },
      });
    } else if (s >= THRESHOLDS.DAM_WARNING_PCT) {
      items.push({
        id: "dam-warning",
        severity: "warning",
        title: { en: "Bang Lang Dam storage high", th: "ระดับน้ำเก็บกักเขื่อนบางลางสูง" },
        evidence: `storage ${pct(s)} (≥${THRESHOLDS.DAM_WARNING_PCT}%) · outflow ${num(dam.outflowCms, 0, " m³/s")}`,
        action: {
          en: "Watch the release schedule daily; rising outflow precedes city flooding by roughly a day.",
          th: "ติดตามแผนการระบายน้ำรายวัน — การเพิ่มการระบายมักนำหน้าน้ำท่วมเมืองราวหนึ่งวัน",
        },
      });
    } else if (s >= THRESHOLDS.DAM_WATCH_PCT) {
      items.push({
        id: "dam-watch",
        severity: "watch",
        title: { en: "Bang Lang Dam filling", th: "เขื่อนบางลางกำลังเติม" },
        evidence: `storage ${pct(s)} (≥${THRESHOLDS.DAM_WATCH_PCT}%)`,
        action: {
          en: "No action yet — keep it on the daily brief.",
          th: "ยังไม่ต้องดำเนินการ — คงไว้ในรายงานประจำวัน",
        },
      });
    }
  }

  // ── River gauges — worst station drives the item ─────────────────────────
  const flooding = gauges.filter((g) => g.status === "flood");
  const warning = gauges.filter((g) => g.status === "warning");
  if (flooding.length > 0) {
    items.push({
      id: "gauge-flood",
      severity: "critical",
      title: {
        en: `${flooding.length} river gauge${flooding.length > 1 ? "s" : ""} at flood level`,
        th: `สถานีวัดน้ำ ${flooding.length} แห่งอยู่ในระดับน้ำท่วม`,
      },
      evidence: flooding.map((g) => `${g.name} ${num(g.levelM, 2, "m")}`).join(" · "),
      action: {
        en: "Evacuate low-lying riverside zones now; open the nearest shelters.",
        th: "อพยพพื้นที่ลุ่มริมน้ำทันที และเปิดศูนย์พักพิงที่ใกล้ที่สุด",
      },
    });
  } else if (warning.length > 0) {
    items.push({
      id: "gauge-warning",
      severity: "warning",
      title: {
        en: `${warning.length} river gauge${warning.length > 1 ? "s" : ""} above warning level`,
        th: `สถานีวัดน้ำ ${warning.length} แห่งเกินระดับเตือนภัย`,
      },
      evidence: warning.map((g) => `${g.name} ${num(g.levelM, 2, "m")}`).join(" · "),
      action: {
        en: "Put riverside sub-districts on notice and stage boats and pumps.",
        th: "แจ้งเตือนตำบลริมน้ำ และเตรียมเรือกับเครื่องสูบน้ำ",
      },
    });
  }

  // ── River forecast — surge needs BOTH a ratio and an absolute floor ──────
  if (riverForecast) {
    const { dischargeCms: now, forecastPeakCms: peak, forecastPeakDay: day, band } = riverForecast;
    if (band === "emergency") {
      items.push({
        id: "river-emergency",
        severity: "critical",
        title: { en: "Pattani River forecast at emergency band", th: "พยากรณ์แม่น้ำปัตตานีอยู่ในระดับฉุกเฉิน" },
        evidence: `discharge ${num(now, 1, " m³/s")} · peak ${num(peak, 1, " m³/s")}${day != null ? ` in ~${day}d` : ""}`,
        action: {
          en: "Activate the provincial flood-response plan; confirm shelter readiness.",
          th: "เปิดใช้แผนเผชิญเหตุอุทกภัยจังหวัด และยืนยันความพร้อมศูนย์พักพิง",
        },
      });
    } else if (
      now != null && peak != null &&
      peak >= now * THRESHOLDS.RIVER_SURGE_RATIO &&
      peak >= THRESHOLDS.RIVER_SURGE_FLOOR_CMS
    ) {
      // Ratio alone is meaningless at low flow — the floor is the point.
      items.push({
        id: "river-surge",
        severity: "warning",
        title: { en: "Pattani River forecast to surge", th: "คาดการณ์แม่น้ำปัตตานีจะเพิ่มขึ้นมาก" },
        evidence: `${num(now, 1, " m³/s")} → peak ${num(peak, 1, " m³/s")} (×${(peak / now).toFixed(1)})${day != null ? ` in ~${day}d` : ""}`,
        action: {
          en: "Use the lead time: clear drainage channels and pre-position pumps downstream.",
          th: "ใช้เวลาที่มี: ลอกท่อระบายน้ำ และวางเครื่องสูบน้ำล่วงหน้าท้ายน้ำ",
        },
      });
    }
  }

  // ── Rain nowcast ────────────────────────────────────────────────────────
  if (precip?.total2hMm != null) {
    const mm = precip.total2hMm;
    if (mm >= THRESHOLDS.RAIN_CRITICAL_2H_MM) {
      items.push({
        id: "rain-critical",
        severity: "critical",
        title: { en: "Heavy rain arriving within 2 hours", th: "ฝนตกหนักจะมาถึงภายใน 2 ชั่วโมง" },
        evidence: `${num(mm, 1, "mm")} forecast in 2h (≥${THRESHOLDS.RAIN_CRITICAL_2H_MM}mm)${precip.minutesToSignificant != null ? ` · starts in ~${precip.minutesToSignificant} min` : ""}`,
        action: {
          en: "Alert drainage crews now and close known flood-prone underpasses.",
          th: "แจ้งทีมระบายน้ำทันที และปิดจุดลอดที่มักน้ำท่วม",
        },
      });
    } else if (mm >= THRESHOLDS.RAIN_WARNING_2H_MM) {
      items.push({
        id: "rain-warning",
        severity: "warning",
        title: { en: "Significant rain in the next 2 hours", th: "ฝนปริมาณมากใน 2 ชั่วโมงข้างหน้า" },
        evidence: `${num(mm, 1, "mm")} forecast in 2h (≥${THRESHOLDS.RAIN_WARNING_2H_MM}mm)`,
        action: {
          en: "Ready drainage crews; watch the gauges over the next few hours.",
          th: "เตรียมทีมระบายน้ำ และเฝ้าระวังสถานีวัดน้ำในชั่วโมงข้างหน้า",
        },
      });
    }
  }

  // ── Flood-affected villages (provincial situation report) ────────────────
  if (floodOps && floodOps.affectedVillages > 0) {
    const worst = floodOps.affectedByDistrict[0];
    items.push({
      id: "villages-affected",
      severity: "watch",
      title: {
        en: `${floodOps.affectedVillages.toLocaleString()} villages reported flood-affected`,
        th: `มีหมู่บ้านประสบอุทกภัย ${floodOps.affectedVillages.toLocaleString()} แห่ง`,
      },
      evidence: `${floodOps.affectedVillages.toLocaleString()} villages · ${floodOps.affectedByDistrict.length} districts${worst ? ` · worst: ${worst.district} (${worst.villages})` : ""}`,
      action: {
        en: "Route relief to the worst-hit district first; the dispatch numbers are in the Flood Ops panel.",
        th: "ส่งความช่วยเหลือไปยังอำเภอที่หนักที่สุดก่อน — เบอร์ติดต่ออยู่ในแผงศูนย์บัญชาการอุทกภัย",
      },
    });
  }

  // ── Air quality — top band only ─────────────────────────────────────────
  const badAir = airDanger.filter(
    (p) => p.dangerBand === "high" || (p.pm25Live != null && p.pm25Live >= THRESHOLDS.AIR_PM25_WARNING),
  );
  if (badAir.length > 0) {
    const worst = [...badAir].sort((a, b) => (b.pm25Live ?? 0) - (a.pm25Live ?? 0))[0];
    items.push({
      id: "air-danger",
      severity: "watch",
      title: { en: "Air quality in the unhealthy band", th: "คุณภาพอากาศอยู่ในระดับที่เป็นอันตราย" },
      evidence: `${worst.provinceNameEn} PM2.5 ${num(worst.pm25Live, 0, " µg/m³")} · ${worst.dangerBand}${worst.washoutBand !== "none" && worst.washoutBand !== "unknown" ? ` · rain relief ${worst.washoutBand}` : ""}`,
      action: {
        en: "Issue a public health advisory for outdoor work and school activity.",
        th: "ออกประกาศเตือนด้านสุขภาพสำหรับงานกลางแจ้งและกิจกรรมโรงเรียน",
      },
    });
  }

  // ── Blind spots — never let a dead feed look like good news ──────────────
  for (const f of feeds) {
    if (f.ok) continue;
    items.push({
      id: `blindspot-${f.id}`,
      severity: "blindspot",
      title: { en: `No data: ${f.label.en}`, th: `ไม่มีข้อมูล: ${f.label.th}` },
      evidence: "feed unavailable — this signal is NOT being monitored",
      action: {
        en: "Treat this signal as unknown, not safe. See docs/RUNBOOK.md to restore the feed.",
        th: "ถือว่าสัญญาณนี้ 'ไม่ทราบ' ไม่ใช่ 'ปลอดภัย' — ดูวิธีกู้คืนที่ docs/RUNBOOK.md",
      },
    });
  }

  return items.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

/** Highest severity present, or null when the board is genuinely clear. */
export function boardSeverity(items: ActionItem[]): ActionSeverity | null {
  const real = items.filter((i) => i.severity !== "blindspot");
  if (real.length === 0) return items.length > 0 ? "blindspot" : null;
  return real[0].severity;
}
